/*
Copyright (c) 2012-2026 Grab Taxi Holdings PTE LTD (GRAB), All Rights Reserved.
NOTICE: All information contained herein is, and remains the property of GRAB.
The intellectual and technical concepts contained herein are confidential,
proprietary and controlled by GRAB and may be covered by patents, patents in process,
and are protected by trade secret or copyright law.

You are strictly forbidden to copy, download, store (in any medium), transmit, disseminate,
adapt or change this material in any way unless prior written permission is obtained from GRAB.
Access to the source code contained herein is hereby forbidden to anyone except current GRAB
employees or contractors with binding Confidentiality and Non-disclosure agreements explicitly
covering such access.

The copyright notice above does not evidence any actual or intended publication or disclosure of
this source code, which includes information that is confidential and/or proprietary,
and is a trade secret, of GRAB.

ANY REPRODUCTION, MODIFICATION, DISTRIBUTION, PUBLIC PERFORMANCE, OR PUBLIC DISPLAY OF OR
THROUGH USE OF THIS SOURCE CODE WITHOUT THE EXPRESS WRITTEN CONSENT OF GRAB IS STRICTLY PROHIBITED,
AND IN VIOLATION OF APPLICABLE LAWS AND INTERNATIONAL TREATIES. THE RECEIPT OR POSSESSION OF THIS
SOURCE CODE AND/OR RELATED INFORMATION DOES NOT CONVEY OR IMPLY ANY RIGHTS TO REPRODUCE,
DISCLOSE OR DISTRIBUTE ITS CONTENTS, OR TO MANUFACTURE, USE, OR SELL ANYTHING THAT IT MAY DESCRIBE,
IN WHOLE OR IN PART.
*/
import { RefObject, useLayoutEffect, useState } from "react"

interface UseViewportFittedSkeletonCountParams {
  containerRef: RefObject<HTMLElement>
  itemHeight: number
  itemGap?: number
  reservedBottomSpace: number
  minItems: number
  maxItems?: number
}

const clamp = (value: number, min: number, max?: number): number =>
  max === undefined ? Math.max(value, min) : Math.min(Math.max(value, min), max)

/**
 * Returns enough placeholders to fill the visible part of a list without
 * coupling the calculation to a page's data or skeleton markup.
 */
export const useViewportFittedSkeletonCount = ({
  containerRef,
  itemHeight,
  itemGap = 0,
  reservedBottomSpace,
  minItems,
  maxItems,
}: UseViewportFittedSkeletonCountParams): number => {
  const [count, setCount] = useState(minItems)

  useLayoutEffect(() => {
    const updateCount = () => {
      const top = containerRef.current?.getBoundingClientRect().top
      if (top === undefined) return

      const availableHeight = window.innerHeight - top - reservedBottomSpace
      const nextCount = clamp(
        Math.floor((availableHeight + itemGap) / (itemHeight + itemGap)),
        minItems,
        maxItems,
      )
      setCount(nextCount)
    }

    updateCount()
    window.addEventListener("resize", updateCount)
    window.addEventListener("orientationchange", updateCount)

    return () => {
      window.removeEventListener("resize", updateCount)
      window.removeEventListener("orientationchange", updateCount)
    }
  }, [
    containerRef,
    itemHeight,
    itemGap,
    reservedBottomSpace,
    minItems,
    maxItems,
  ])

  return count
}
